import * as DialogPrimitive from "@radix-ui/react-dialog";
import { Link } from "@tanstack/react-router";
import { Building2, ClipboardList, FileText, Star, X } from "lucide-react";
import { useState } from "react";
import { TabBar } from "@/components/ui/tab-bar";
import { type Favorite, type FavoriteKind, removeFavorite, useFavorites } from "@/lib/favorites";
import { cn } from "@/lib/utils";

interface HomeFavoritesDrawerProps {
  open: boolean;
  onOpenChange: (next: boolean) => void;
}

// Right-side slide-out for the home page. Favorites only — Recent searches
// live inline on the home page itself. Kind sub-tabs (Policies / Claims /
// Agencies) split the list so users with a mixed pin set can find a single
// kind without scanning.

const KIND_META: Record<FavoriteKind, { Icon: typeof FileText; label: string }> = {
  policy: { Icon: FileText, label: "Policy" },
  claim: { Icon: ClipboardList, label: "Claim" },
  agency: { Icon: Building2, label: "Agency" },
};

export function HomeFavoritesDrawer({ open, onOpenChange }: HomeFavoritesDrawerProps) {
  return (
    <DialogPrimitive.Root open={open} onOpenChange={onOpenChange}>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay className="data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 fixed inset-0 z-50 bg-black/50" />
        <DialogPrimitive.Content className="data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:slide-out-to-right data-[state=open]:slide-in-from-right fixed inset-y-0 right-0 z-50 flex w-full max-w-sm flex-col border-l bg-card shadow-xl duration-300">
          <DialogPrimitive.Title className="sr-only">Favorites</DialogPrimitive.Title>
          <DialogPrimitive.Description className="sr-only">
            Your pinned policies, claims, and agencies. Open any dashboard and click the star to pin
            it here.
          </DialogPrimitive.Description>
          <div className="flex items-center justify-between border-b px-3 py-2">
            <span className="inline-flex items-center gap-1.5 font-semibold text-sm">
              <Star className="size-4 text-amber-500" aria-hidden="true" />
              Favorites
            </span>
            <DialogPrimitive.Close
              aria-label="Close"
              className="inline-flex size-7 items-center justify-center rounded-md text-muted-foreground hover:bg-accent hover:text-foreground"
            >
              <X className="size-4" aria-hidden="true" />
            </DialogPrimitive.Close>
          </div>
          <div className="min-h-0 flex-1 overflow-y-auto">
            <FavoritesList />
          </div>
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
}

function FavoritesList() {
  const all = useFavorites();
  const [kindTab, setKindTab] = useState<FavoriteKind>("policy");

  const counts: Record<FavoriteKind, number> = { policy: 0, claim: 0, agency: 0 };
  for (const item of all) counts[item.kind]++;

  const visible = all.filter((item) => item.kind === kindTab);

  const tabs: { key: FavoriteKind; label: string }[] = [
    { key: "policy", label: counts.policy > 0 ? `Policies (${counts.policy})` : "Policies" },
    { key: "claim", label: counts.claim > 0 ? `Claims (${counts.claim})` : "Claims" },
    { key: "agency", label: counts.agency > 0 ? `Agencies (${counts.agency})` : "Agencies" },
  ];

  return (
    <>
      <div className="border-b px-3 pt-2">
        <TabBar
          ariaLabel="Favorites by kind"
          activeKey={kindTab}
          onChange={setKindTab}
          tabs={tabs}
        />
      </div>
      {visible.length === 0 ? (
        <p className="px-4 py-6 text-center text-muted-foreground text-sm">
          {all.length === 0
            ? "No pinned items yet. Open a policy, claim, or agency and click the ★ in the header to pin it here."
            : `No pinned ${kindTab === "policy" ? "policies" : kindTab === "claim" ? "claims" : "agencies"} yet.`}
        </p>
      ) : (
        <ul>
          {visible.map((item) => (
            <FavoriteRow key={`${item.kind}-${item.number}`} favorite={item} />
          ))}
        </ul>
      )}
    </>
  );
}

function FavoriteRow({ favorite }: { favorite: Favorite }) {
  const meta = KIND_META[favorite.kind];
  const linkProps =
    favorite.kind === "policy"
      ? ({ to: "/policy/$policyNumber", params: { policyNumber: favorite.number } } as const)
      : favorite.kind === "agency"
        ? ({ to: "/agency/$agentNumber", params: { agentNumber: favorite.number } } as const)
        : ({ to: "/claims/$claimNumber", params: { claimNumber: favorite.number } } as const);
  return (
    <li
      className={cn(
        "group flex items-center gap-3 border-b px-4 py-2 text-sm last:border-0 hover:bg-muted/30",
      )}
    >
      <meta.Icon className="size-4 shrink-0 text-muted-foreground" aria-hidden="true" />
      <Link {...linkProps} className="font-medium text-primary hover:underline">
        {favorite.number}
      </Link>
      {favorite.name && <span className="truncate text-muted-foreground">{favorite.name}</span>}
      <button
        type="button"
        onClick={() => removeFavorite(favorite.kind, favorite.number)}
        aria-label={`Remove ${favorite.number} from favorites`}
        title="Remove from favorites"
        className="ml-auto rounded-md p-1 text-muted-foreground opacity-0 transition-opacity hover:bg-accent hover:text-foreground group-hover:opacity-100 focus:opacity-100"
      >
        <X className="size-3.5" aria-hidden="true" />
      </button>
    </li>
  );
}
